'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CountUp from '@/components/CountUp';
import { SUPABASE_ANON_KEY, CONTACT_EMAIL_ENDPOINT } from '@/lib/supabase/config';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll(
      '.reveal, .reveal-left, .reveal-right, .reveal-scale'
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const stats = [
  { number: '10+', label: 'Years Playing' },
  { number: '20+', label: 'Tracks Recorded' },
  { number: '7 Day', label: 'Average Turnaround' },
];

const specialisms = [
  {
    title: 'REMOTE SESSION DRUMMING',
    desc: 'High-quality drum tracks recorded in a professional studio environment, delivered as individual stem files. Drop-in ready for your mix.',
  },
  {
    title: 'IN-PERSON SESSIONS',
    desc: 'Available for studio bookings. Bring your project in and track live drums with full control over the sound and performance.',
  },
  {
    title: 'PROGRAMMED DRUM PRODUCTION',
    desc: 'Realistic drum programming using industry-standard sample libraries. Ideal for tight budgets or pre-production demos.',
  },
  {
    title: 'LIVE PERFORMANCE',
    desc: 'Available for live shows, tours, and one-off performances. Inquire with full event details and dates.',
  },
];

const drumsGear = [
  'SJC Custom Drums Pathfinder Series Shells',
  'SJC Pathfinder 14x6.5 snare',
  'Meinl Cymbals (Byzance and Classics Custom Series)',
  'DW 9000 and 3000 Series Hardware',
  'Evans Drum Heads',
  'Axis Longboard Pedals',
  'OnTrigger Kick Triggers',
];
const recordingGear = [
  'Logic Pro X',
  '16 channel interface',
  'Shure SM7B (Kick In)',
  'AKG D112 (Kick Out)',
  'Shure SM57 (Snare)',
  'Neumann KM184 (Overheads)',
  'Yamaha HS8 Studio Monitors',
];

const audioTracks = [
  {
    label: 'ALTAR — DRUM PLAYTHROUGH',
    band: 'Covert UK',
    genre: 'METALCORE',
    youtubeId: '2l6OQFlcLWw',
  },
  {
    label: 'NOTHING MORE — DRUM PLAYTHROUGH',
    band: 'Dystopian Sun',
    genre: 'DEATHCORE',
    youtubeId: '3nU6wFoxTxo',
  },
  {
    label: 'LIVE SESSION FOOTAGE',
    band: 'Diggity Glover',
    genre: 'HEAVY MUSIC',
    youtubeId: 'O2YEO4FFa7s',
  },
];

const processSteps = [
  {
    num: '01',
    title: 'GET IN TOUCH',
    desc: 'Fill out the booking form below with your project details, genre, tempo, and any reference tracks.',
  },
  {
    num: '02',
    title: 'SEND YOUR TRACKS',
    desc: 'Share your guide tracks, click track, and any scratch recordings via the file link provided.',
  },
  {
    num: '03',
    title: 'TRACKING',
    desc: "Drums are recorded and edited to the grid. You\'ll receive a progress update before final files are sent.",
  },
  {
    num: '04',
    title: 'REVISIONS',
    desc: 'Up to 2 rounds of performance or edit revisions included as standard.',
  },
  {
    num: '05',
    title: 'DELIVERY',
    desc: 'Individual stem files delivered as high-resolution WAV, labelled and ready for your mixer.',
  },
];

const pricingCards = [
  {
    name: 'SINGLE TRACK',
    price: 'Get a Quote',
    unit: 'per track',
    featured: false,
    inclusions: ['Full drum performance', 'Editing to grid', 'Stem delivery', '1 revision'],
  },
  {
    name: 'EP / BUNDLE',
    price: 'Get a Quote',
    unit: 'up to 5 tracks',
    featured: true,
    inclusions: [
      'Full drum performances',
      'Editing to grid',
      'Stem delivery',
      '2 revisions',
      'Priority turnaround',
    ],
  },
  {
    name: 'PROGRAMMED DRUMS',
    price: 'Get a Quote',
    unit: 'per track',
    featured: false,
    inclusions: ['Realistic MIDI drum programming', 'Mix-ready samples', '1 revision'],
  },
];

const genres = [
  'METALCORE',
  'DEATHCORE',
  'HARDCORE',
  'DJENT',
  'PROGRESSIVE METAL',
  'POST-HARDCORE',
  'HEAVY ROCK',
];

const testimonials = [
  {
    quote:
      "Working with Diggs as a session drummer has been an absolute pleasure. He quickly picked up our songs from rough demos and came in fully prepared, making the whole process smooth and efficient. He brings all his own high-quality gear, so everything sounds professional right out of the gate, and he's incredibly easy to work with — he listens carefully to what the band wants and keeps sessions productive and relaxed.",
    name: 'Wosh',
    band: 'Session Drumming',
  },
  {
    quote:
      "Diggity has run our live sound on multiple occasions and always been diligent with giving us the best he possibly can. Going above and beyond with extra flare and effects helps elevate our live show. He's professional and calm, and confronts issues in a straightforward, solution-driven manner. We'd be happy to work with him again any time.",
    name: 'Only The Righteous',
    band: 'Live Sound Mixing',
  },
];

const faqItems = [
  {
    q: 'Do you record remotely or in-person only?',
    a: 'Both. Remote sessions are the most common — you send your tracks and receive stems back within the agreed turnaround. In-person studio bookings are also available on request.',
  },
  {
    q: 'What do I need to send you before we start?',
    a: 'A click track, guide tracks (guitar/bass/scratch vocals), tempo map, and any reference tracks that capture the feel you are going for. The more context the better.',
  },
  {
    q: 'What tunings and time signatures do you work in?',
    a: 'All standard and extended tunings from E standard down to 8-string drop territory. Odd time signatures, polyrhythms, and tempo changes are all handled — just flag them in your brief.',
  },
  {
    q: 'Can you track to a click or programmed guide?',
    a: 'Yes — tracking to a click is standard practice. Programmed drum guides are also welcome as a reference for feel and arrangement.',
  },
  {
    q: 'Do you offer triggered drum sounds or acoustic only?',
    a: 'Both options are available. Triggered samples can be blended with the acoustic recording, or a fully triggered/programmed approach can be used depending on the project.',
  },
  {
    q: 'How are the final files delivered?',
    a: 'Individual stem files (kick, snare, hi-hat, toms, overheads, room) delivered as 24-bit WAV at your session sample rate, labelled and consolidated from bar 1.',
  },
];

const panelHeader = (title: string) => (
  <div
    style={{
      background: '#ff1a1a',
      color: '#000000',
      fontFamily: 'Manrope, sans-serif',
      fontWeight: 700,
      fontSize: '0.8rem',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      padding: '8px 16px',
    }}
  >
    {title}
  </div>
);

export default function SessionDrummingPage() {
  useScrollReveal();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    bandName: '',
    sessionType: '',
    numTracks: '',
    tempo: '',
    tuning: '',
    referenceLink: '',
    notes: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    setFormLoading(true);
    setFormError(null);
    try {
      const res = await fetch(CONTACT_EMAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          band: formData.bandName,
          service: `Session Drumming — ${formData.sessionType || 'General enquiry'}`,
          budget: '',
          details: `Session type: ${formData.sessionType || '—'}\nNumber of tracks: ${formData.numTracks || '—'}\nTempo: ${formData.tempo || '—'}\nTuning: ${formData.tuning || '—'}\nReference: ${formData.referenceLink || '—'}\n\nNotes:\n${formData.notes || '—'}`,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error)
        throw new Error(data.error || 'Something went wrong. Please try again.');
      setFormSubmitted(true);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    background: '#1a1a1a',
    border: '1px solid #ff1a1a',
    color: '#ffffff',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '1rem',
    padding: '10px 14px',
    width: '100%',
    outline: 'none',
    borderRadius: 0,
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '0.7rem',
    letterSpacing: '0.15em',
    textTransform: 'uppercase' as const,
    color: '#cccccc',
    display: 'block',
    marginBottom: '8px',
  };

  return (
    <div
      style={{ minHeight: '100vh', background: '#000000', color: '#ffffff', overflowX: 'hidden' }}
    >
      <Header />

      {/* ─── HERO ─── */}
      <section
        style={{
          padding: '60px 24px 48px',
          borderBottom: '1px solid #ff1a1a',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '420px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: "url('/assets/images/diggity-8.jpg')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)' }} />
        </div>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,26,26,0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
          <p
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#ff1a1a',
              marginBottom: '16px',
            }}
          >
            // Services
          </p>
          <h1
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontSize: 'clamp(3.5rem, 10vw, 8rem)',
              lineHeight: '0.9',
              color: '#ffffff',
              textShadow: '0 0 20px rgba(255,26,26,0.7)',
              marginBottom: '24px',
            }}
          >
            SESSION
            <br />
            <span style={{ color: '#ff1a1a' }}>DRUMMING</span>
          </h1>
          <p
            style={{
              fontFamily: 'Manrope, sans-serif',
              color: '#cccccc',
              fontSize: '1.1rem',
              maxWidth: '500px',
              lineHeight: '1.7',
              marginBottom: '32px',
            }}
          >
            Precision drumming for Metalcore, Deathcore and heavy music. Remote and in-person
            sessions available.
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <a
              href="#book"
              className="btn-primary"
              style={{ fontSize: '0.9rem', padding: '14px 36px' }}
            >
              Book a Session
            </a>
            <a
              href="#audio"
              className="btn-outline"
              style={{ fontSize: '0.9rem', padding: '14px 36px' }}
            >
              Hear My Playing
            </a>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <div style={{ background: '#111111', borderBottom: '1px solid rgba(255,26,26,0.3)' }}>
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          }}
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="reveal"
              style={{
                padding: '28px 20px',
                textAlign: 'center',
                borderRight: i < stats.length - 1 ? '1px solid rgba(255,26,26,0.3)' : 'none',
              }}
            >
              <div
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  lineHeight: '1',
                  color: '#ffffff',
                  textShadow: '0 0 10px rgba(255,26,26,0.5)',
                  marginBottom: '8px',
                }}
              >
                <CountUp value={stat.number} />
              </div>
              <div
                style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#ff1a1a',
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── SPECIALISMS ─── */}
      <section style={{ padding: '60px 24px', borderBottom: '1px solid rgba(255,26,26,0.3)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // What I Do
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              What I Do
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '12px',
            }}
          >
            {specialisms.map((s, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{
                  background: '#1a1a1a',
                  border: '1px solid #ff1a1a',
                  borderTop: '3px solid #ff1a1a',
                }}
              >
                {panelHeader(s.title)}
                <div style={{ padding: '20px' }}>
                  <p
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.95rem',
                      lineHeight: '1.7',
                    }}
                  >
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GENRES ─── */}
      <section
        style={{
          padding: '40px 24px',
          borderBottom: '1px solid rgba(255,26,26,0.3)',
          background: '#111111',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <p
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#666666',
              marginBottom: '16px',
            }}
          >
            // Genres I Work In
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {genres.map((g) => (
              <span
                key={g}
                style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.1em',
                  color: '#ff1a1a',
                  border: '1px solid rgba(255,26,26,0.4)',
                  padding: '4px 12px',
                }}
              >
                {g}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GEAR ─── */}
      <section style={{ padding: '60px 24px', borderBottom: '1px solid rgba(255,26,26,0.3)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Equipment
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              The Kit
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '12px',
            }}
          >
            <div className="reveal" style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}>
              {panelHeader('Drums & Hardware')}
              <ul style={{ padding: '16px', display: 'flex', flexDirection: 'column' }}>
                {drumsGear.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.95rem',
                      padding: '10px 0',
                      borderBottom:
                        i < drumsGear.length - 1 ? '1px solid rgba(255,26,26,0.15)' : 'none',
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="reveal" style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}>
              {panelHeader('Recording Setup')}
              <ul style={{ padding: '16px', display: 'flex', flexDirection: 'column' }}>
                {recordingGear.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.95rem',
                      padding: '10px 0',
                      borderBottom:
                        i < recordingGear.length - 1 ? '1px solid rgba(255,26,26,0.15)' : 'none',
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── AUDIO / VIDEO ─── */}
      <section
        id="audio"
        style={{
          padding: '60px 24px',
          borderBottom: '1px solid rgba(255,26,26,0.3)',
          background: '#111111',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Listen
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Hear My Playing
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '12px',
            }}
          >
            {audioTracks.map((track, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}
              >
                {panelHeader(track.label)}
                <div style={{ padding: '0' }}>
                  <div
                    style={{
                      position: 'relative',
                      paddingBottom: '56.25%',
                      height: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${track.youtubeId}`}
                      title={track.label}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: '12px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        color: '#cccccc',
                        fontSize: '0.85rem',
                      }}
                    >
                      {track.band}
                    </span>
                    <span
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.65rem',
                        color: '#ff1a1a',
                        letterSpacing: '0.1em',
                      }}
                    >
                      {track.genre}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROCESS ─── */}
      <section style={{ padding: '60px 24px', borderBottom: '1px solid rgba(255,26,26,0.3)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // How It Works
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              The Process
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {processSteps.map((step, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{
                  display: 'flex',
                  gap: '24px',
                  padding: '20px 0',
                  borderBottom: '1px solid rgba(255,26,26,0.2)',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    color: '#ff1a1a',
                    border: '1px solid #ff1a1a',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background: 'rgba(255,26,26,0.08)',
                  }}
                >
                  {step.num}
                </div>
                <div>
                  <h3
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.9rem',
                      letterSpacing: '0.1em',
                      color: '#ffffff',
                      marginBottom: '8px',
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.9rem',
                      lineHeight: '1.7',
                    }}
                  >
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section
        style={{
          padding: '60px 24px',
          borderBottom: '1px solid rgba(255,26,26,0.3)',
          background: '#111111',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Rates
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Pricing
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '12px',
            }}
          >
            {pricingCards.map((card, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{
                  background: '#1a1a1a',
                  border: card.featured ? '2px solid #ff1a1a' : '1px solid #ff1a1a',
                  boxShadow: card.featured ? '0 0 20px rgba(255,26,26,0.3)' : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {panelHeader(card.name + (card.featured ? ' ★ FEATURED' : ''))}
                <div
                  style={{
                    padding: '24px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 900,
                        fontSize: '2rem',
                        color: '#ff1a1a',
                        textShadow: '0 0 8px rgba(255,26,26,0.5)',
                        display: 'block',
                      }}
                    >
                      {card.price}
                    </span>
                    <span
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.7rem',
                        color: '#666666',
                        letterSpacing: '0.1em',
                      }}
                    >
                      {card.unit}
                    </span>
                  </div>
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                    {card.inclusions.map((inc, j) => (
                      <li
                        key={j}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          fontFamily: 'Manrope, sans-serif',
                          color: '#cccccc',
                          fontSize: '0.9rem',
                        }}
                      >
                        <span style={{ color: '#ff1a1a', flexShrink: 0 }}>✓</span>
                        {inc}
                      </li>
                    ))}
                  </ul>
                  <Link
                    href="/contact"
                    className="btn-primary"
                    style={{ fontSize: '0.8rem', padding: '10px 20px', textAlign: 'center' }}
                  >
                    Book Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section style={{ padding: '60px 24px', borderBottom: '1px solid rgba(255,26,26,0.3)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Testimonials
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              What Bands Say
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '12px',
            }}
          >
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}
              >
                {panelHeader('Testimonial')}
                <div style={{ padding: '20px' }}>
                  <p
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.95rem',
                      lineHeight: '1.8',
                      marginBottom: '16px',
                    }}
                  >
                    "{t.quote}"
                  </p>
                  <div style={{ borderTop: '1px solid rgba(255,26,26,0.3)', paddingTop: '12px' }}>
                    <p
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 700,
                        color: '#ffffff',
                        fontSize: '0.9rem',
                      }}
                    >
                      {t.name}
                    </p>
                    <p
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.7rem',
                        color: '#ff1a1a',
                        letterSpacing: '0.1em',
                        marginTop: '4px',
                      }}
                    >
                      {t.band}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section
        style={{
          padding: '60px 24px',
          borderBottom: '1px solid rgba(255,26,26,0.3)',
          background: '#111111',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // FAQ
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Common Questions
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {faqItems.map((item, i) => (
              <div
                key={i}
                className="reveal"
                style={{ border: '1px solid #ff1a1a', background: '#1a1a1a' }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    background: openFaq === i ? 'rgba(255,26,26,0.1)' : 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    padding: '14px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.95rem',
                    transition: 'background 0.2s',
                  }}
                >
                  {item.q}
                  <span style={{ color: '#ff1a1a', flexShrink: 0, marginLeft: '16px' }}>
                    {openFaq === i ? '▲' : '▼'}
                  </span>
                </button>
                {openFaq === i && (
                  <div
                    style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(255,26,26,0.3)' }}
                  >
                    <p
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        color: '#cccccc',
                        fontSize: '0.95rem',
                        lineHeight: '1.7',
                        paddingTop: '12px',
                      }}
                    >
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BOOKING FORM ─── */}
      <section id="book" style={{ padding: '60px 24px 80px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Book
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Book a Session
            </h2>
          </div>
          {formSubmitted ? (
            <div className="reveal" style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}>
              {panelHeader('Booking Received')}
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <h3
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 900,
                    fontSize: '1.8rem',
                    color: '#ffffff',
                    textShadow: '0 0 10px rgba(255,26,26,0.5)',
                    marginBottom: '16px',
                  }}
                >
                  Booking Received.
                </h3>
                <p
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    color: '#cccccc',
                    fontSize: '1rem',
                    lineHeight: '1.7',
                  }}
                >
                  Expect a reply within 24–48 hours.
                </p>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleFormSubmit}
                noValidate
              className="reveal"
              style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}
            >
              {panelHeader('Session Drumming Enquiry')}
              <div
                style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                  }}
                >
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleFormChange}
                      required
                      placeholder="Your name"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      placeholder="your@email.com"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Band / Project Name</label>
                  <input
                    type="text"
                    name="bandName"
                    value={formData.bandName}
                    onChange={handleFormChange}
                    placeholder="Your band name"
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                  }}
                >
                  <div>
                    <label style={labelStyle}>Session Type *</label>
                    <select
                      name="sessionType"
                      value={formData.sessionType}
                      onChange={handleFormChange}
                      required
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="" disabled>
                        Select...
                      </option>
                      {[
                        'Remote Session',
                        'In-Person Session',
                        'Programmed Drums',
                        'Live Performance',
                      ].map((s) => (
                        <option key={s} value={s} style={{ background: '#1a1a1a' }}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Number of Tracks</label>
                    <input
                      type="text"
                      name="numTracks"
                      value={formData.numTracks}
                      onChange={handleFormChange}
                      placeholder="e.g. 3"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                  }}
                >
                  <div>
                    <label style={labelStyle}>Tempo (BPM)</label>
                    <input
                      type="text"
                      name="tempo"
                      value={formData.tempo}
                      onChange={handleFormChange}
                      placeholder="e.g. 180"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Tuning</label>
                    <input
                      type="text"
                      name="tuning"
                      value={formData.tuning}
                      onChange={handleFormChange}
                      placeholder="e.g. Drop A"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Reference Track Link</label>
                  <input
                    type="text"
                    name="referenceLink"
                    value={formData.referenceLink}
                    onChange={handleFormChange}
                    placeholder="YouTube / Spotify link"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Additional Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={4}
                    placeholder="Genre, feel, anything else..."
                    style={{ ...inputStyle, resize: 'none' }}
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={formLoading}
                    className="btn-primary"
                    style={{
                      fontSize: '0.9rem',
                      padding: '14px 36px',
                      opacity: formLoading ? 0.6 : 1,
                      cursor: formLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {formLoading ? 'Sending...' : 'Submit Booking'}
                  </button>
                  {formError && (
                    <p
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.75rem',
                        color: '#ff1a1a',
                        marginTop: '12px',
                      }}
                    >
                      ⚠ {formError}
                    </p>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
